/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * checkInResults
 * --------------
 * The pure half of the History picker: which events it lists and in what
 * order, and which one it opens on. No requests, no React — `now` is passed in.
 */

import { parseStoredUtc } from '../../../utils/zonedDateTime';
import { eventWindow } from './checkInRules';

/** Events that can never have check-ins, so the picker leaves them out. */
const HIDDEN_STATUSES = new Set(['DRAFT', 'CANCELLED']);

const startOf = (event) => parseStoredUtc(event.start_datetime)?.getTime() ?? 0;
const endOf = (event) => parseStoredUtc(event.end_datetime)?.getTime() ?? 0;

/**
 * The picker's three groups:
 *   open      open for check-in now, soonest start first
 *   finished  over, newest first — last week's event sits near the top
 *   upcoming  not open yet, soonest first
 */
export function groupEvents(events, now) {
  const groups = { open: [], finished: [], upcoming: [] };
  (events || []).forEach((event) => {
    if (HIDDEN_STATUSES.has(String(event.status || '').toUpperCase())) return;
    if (!event.start_datetime || !event.end_datetime) return;
    if (eventWindow(event, now).isOpen) groups.open.push(event);
    else if (endOf(event) < now.getTime()) groups.finished.push(event);
    else groups.upcoming.push(event);
  });
  groups.open.sort((a, b) => startOf(a) - startOf(b));
  groups.finished.sort((a, b) => endOf(b) - endOf(a));
  groups.upcoming.sort((a, b) => startOf(a) - startOf(b));
  return groups;
}

/** The event History opens on: one open now, else the latest finished, else the next upcoming. */
export function defaultEventId(groups) {
  const first = groups.open[0] || groups.finished[0] || groups.upcoming[0];
  return first ? first.id : null;
}

/**
 * One activity's cap, or null when it has none.
 *
 * Blank, null and 0 all mean UNCAPPED, not "a cap of nothing" — the same rule
 * `remainingSeats` applies on the participant side, and the one the organizer
 * form writes: an emptied capacity box is how "no limit" is saved. Reading 0
 * as a real cap would report an unlimited event as full. Nothing in the
 * database forbids a 0 (there is no positive-capacity CHECK constraint), so
 * this is the only place that distinction is made.
 */
function capOf(value) {
  const capacity = Number(value);
  return Number.isFinite(capacity) && capacity > 0 ? capacity : null;
}

/** A count or amount from the engine, which serialises numerics as strings. */
function numOf(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * The `event_activity_breakdown` saved query, as the History tab reads it.
 *
 * Returns the activities in the order the query sorted them, plus the totals
 * that back "All activities". THE TOTALS ARE SUMMED FROM THE SAME ROWS the
 * per-activity view shows, so the two can never disagree — picking an activity
 * and picking All are two readings of one answer, not two queries.
 *
 * Columns are found BY NAME. The engine returns whatever the saved SQL
 * selected, so reading by index would mis-read the moment a column is added,
 * and a column that is absent reads as zero rather than NaN.
 */
export function breakdownFromResult(result) {
  const columns = result?.columns || [];
  const at = (wanted) => columns.findIndex((column) => column?.name === wanted);
  const idAt = at('activity_id');
  const nameAt = at('activity');
  const placesAt = at('places');
  const cell = (row, index) => (index < 0 ? undefined : row[index]);

  if (!Array.isArray(result?.rows) || nameAt < 0) {
    return { activities: [], totals: emptyTotals() };
  }

  const activities = result.rows.map((row, index) => ({
    id: cell(row, idAt) ?? index,
    name: cell(row, nameAt),
    capacity: capOf(cell(row, placesAt)),
    // The FEE, not the computed revenue. A zero revenue cannot tell a free
    // activity from a paid one nobody has confirmed yet, and only the second
    // should show a money tile.
    fee: numOf(cell(row, at('fee'))),
    registrations: numOf(cell(row, at('registrations'))),
    confirmed: numOf(cell(row, at('confirmed'))),
    checkedIn: numOf(cell(row, at('checked_in'))),
    checkedOut: numOf(cell(row, at('checked_out'))),
    expected: numOf(cell(row, at('expected_revenue'))),
  }));

  const capped = activities.filter((a) => a.capacity !== null);
  const sum = (key) => activities.reduce((total, a) => total + a[key], 0);
  return {
    activities,
    totals: {
      // Null, not 0, when nothing is capped: "unlimited" and "no places" are
      // opposite answers and 0 renders as the second.
      capacity: capped.length ? capped.reduce((t, a) => t + a.capacity, 0) : null,
      cappedCount: capped.length,
      registrations: sum('registrations'),
      confirmed: sum('confirmed'),
      checkedIn: sum('checkedIn'),
      checkedOut: sum('checkedOut'),
      expected: sum('expected'),
      // Any priced activity makes the event a paying one, even when its
      // siblings are free.
      priced: activities.some((a) => a.fee > 0),
    },
  };
}

function emptyTotals() {
  return {
    capacity: null, cappedCount: 0, registrations: 0,
    confirmed: 0, checkedIn: 0, checkedOut: 0, expected: 0, priced: false,
  };
}
